import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/ems/DashboardLayout';
import { exercisesApi, injectsApi, timelineApi, responsesApi, injectLibraryApi, uploadsApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useWebSocket } from '../../context/WebSocketContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import InjectQuickViewModal from '../../components/ems/InjectQuickViewModal';
import ActivityLogForm from '../../components/ems/ActivityLogForm';
import UnifiedActivityStream from '../../components/ems/UnifiedActivityStream';
import Softphone from '../../components/ems/Softphone';
import QuickMessageButton from '../../components/ems/QuickMessageButton';
import { TimezoneBadge, TimezoneTime } from '../../components/shared/TimezoneIndicator';
import { formatTimeInTimezone } from '../../lib/timezone';
import { 
  ArrowLeft,
  PlayCircle,
  PauseCircle,
  StopCircle,
  Send,
  Plus,
  MessageSquare,
  Clock,
  Activity,
  Wifi,
  WifiOff,
  AlertTriangle,
  CheckCircle,
  Filter,
  ArrowUpDown,
  Search,
  FileText,
  Phone
} from 'lucide-react';

// Category options for filtering
const INJECT_CATEGORIES = [
  'All',
  'Operations',
  'Planning', 
  'Logistics',
  'Finance & Admin',
  'Command'
];

export default function ControlRoomPage() {
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { connected, joinExercise, leaveExercise } = useWebSocket();
  
  const [exercise, setExercise] = useState(null);
  const [injects, setInjects] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteForm, setNoteForm] = useState({
    type: 'General',
    message: '',
    objectiveMet: 'Yes',
    attachment: null,
  });
  const [noteInjectId, setNoteInjectId] = useState('');
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [endConfirmOpen, setEndConfirmOpen] = useState(false);
  const [statusChangeOpen, setStatusChangeOpen] = useState(false);

  // Filters for All Injects
  const [searchFilter, setSearchFilter] = useState('');
  const [recipientFilter, setRecipientFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');

  // Old EMS-style View modal
  const [viewOpen, setViewOpen] = useState(false);
  const [viewInject, setViewInject] = useState(null);
  
  // Quick inject dialog
  const [quickInjectOpen, setQuickInjectOpen] = useState(false);
  const [editingCRInjectId, setEditingCRInjectId] = useState(null);
  const [quickInjectForm, setQuickInjectForm] = useState({
    title: '',
    content: '',
    inject_time: '',
    method: '',
    dependancy: '',
    recipient: '',
    specific_function: '',
    objective: '',
    reference: '',
    injected_by: '',
    supplemental_information: '',
    key_outcomes: '',
    priority: 'medium',
    category: 'Operations'
  });
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryTemplates, setLibraryTemplates] = useState([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [selectedLibraryTemplateId, setSelectedLibraryTemplateId] = useState(null);

  const [libraryCategory, setLibraryCategory] = useState('all');
  const [libraryCompany, setLibraryCompany] = useState('all');
  const [libraryCompanies, setLibraryCompanies] = useState([]);
  const [libraryCategories, setLibraryCategories] = useState([]);

  
  const timelineEndRef = useRef(null);

  // Load library templates when Quick Inject dialog opens
  useEffect(() => {
    if (quickInjectOpen && showLibrary) {
      injectLibraryApi.listCompanies().then((res) => setLibraryCompanies(res?.companies || [])).catch(() => setLibraryCompanies([]));
      injectLibraryApi.listCategories().then((res) => setLibraryCategories(res?.categories || [])).catch(() => setLibraryCategories([]));

      if (libraryCategory !== 'all' || libraryCompany !== 'all' || librarySearch.trim()) {
        loadLibraryTemplates();
      } else {
        setLibraryTemplates([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickInjectOpen, showLibrary, librarySearch, libraryCategory, libraryCompany]);

  const getScheduledTime = (inject) => {
    const meta = inject?.metadata || {};
    return (
      meta['Time of Inject (UTC)'] ||
      meta['Inject Time'] ||
      meta['Inject Time (UTC)'] ||
      meta.time_of_inject_utc ||
      meta.inject_time ||
      meta.time_of_inject ||
      null
    );
  };

  const getRecipientLabel = (inject) => {
    const meta = inject?.metadata || {};
    return (
      meta.recipient ||
      meta.Recipient ||
      meta['Recipient'] ||
      meta['Inject Category'] ||
      meta.inject_category ||
      inject?.recipient ||
      inject?.category ||
      '-'
    );
  };

  const getInjectIdLabel = (inject) => {
    const meta = inject?.metadata || {};
    return (
      meta['Inject ID'] ||
      meta.inject_id ||
      inject?.inject_number ||
      inject?.inject_id ||
      '-'
    );
  };

  const getDisplayTime = (inject) => {
    const scheduled = getScheduledTime(inject);
    if (scheduled) return String(scheduled);
    if (inject?.triggered_at) return new Date(inject.triggered_at).toLocaleTimeString();
    if (inject?.created_at) return new Date(inject.created_at).toLocaleTimeString();
    return '-';
  };

  const getScheduledSortValue = (inject) => {
    const scheduled = getScheduledTime(inject);
    if (!scheduled) return null;
    const cleaned = String(scheduled)
      .replace(/(st|nd|rd|th)/g, '')
      .replace('@', ' ')
      .trim();
    const parsed = Date.parse(cleaned);
    if (!Number.isNaN(parsed)) return parsed;
    return cleaned.toLowerCase();
  };

  const buildInjectMetadata = (form) => {
    const raw = {
      'Time of Inject (UTC)': form.inject_time,
      'Inject Time': form.inject_time,
      Method: form.method,
      Dependancy: form.dependancy,
      Recipient: form.recipient,
      'Specific Function': form.specific_function,
      Objective: form.objective,
      'Objective ID': form.reference,
      'Inject Information': form.content,
      'Injected By': form.injected_by,
      'Supplemental Information': form.supplemental_information,
      'Key Outcomes': form.key_outcomes,
    };
    return Object.fromEntries(Object.entries(raw).filter(([, v]) => v));
  };

  useEffect(() => {
    if (quickInjectOpen && showLibrary) loadLibraryTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [librarySearch, libraryCategory, libraryCompany]);

  const loadLibraryTemplates = async () => {
    try {
      const data = await injectLibraryApi.list({
        search: librarySearch,
        category: libraryCategory,
        company: libraryCompany,
        limit: 5000
      });
      setLibraryTemplates(data?.items || []);
    } catch (err) {
      console.error('Failed to load library:', err);
      setLibraryTemplates([]);
    }
  };

  const handleSelectTemplate = (template) => {
    setQuickInjectForm({
      title: template.inject_information || template.objective || template.title || template.inject_title || template.inject_id || '',
      content: template.supplemental_information || template.key_outcomes || template.content || template.inject_content || template.description || '',
      inject_time: template.time_of_inject_utc || template.inject_time || template['Time of Inject (UTC)'] || '',
      method: template.method || '',
      dependancy: template.dependancy || template.dependency || '',
      recipient: template.recipient || '',
      specific_function: template.specific_function || '',
      objective: template.objective || '',
      reference: template.objective_id || '',
      injected_by: template.injected_by || '',
      supplemental_information: template.supplemental_information || '',
      key_outcomes: template.key_outcomes || '',
      priority: template.priority || 'medium',
      category: template.category || 'Operations'
    });
    setSelectedLibraryTemplateId(template.template_id);
    setShowLibrary(false);
  };

  useEffect(() => {
    loadData();
    joinExercise(exerciseId);
    
    const handleWsMessage = (event) => {
      const data = event.detail;
      if (data.exercise_id === exerciseId || !data.exercise_id) {
        if (data.type === 'inject_triggered') {
          loadInjects();
          loadTimeline();
        }
        if (data.type === 'inject_updated' || data.type === 'inject_response') {
          // V14.5 BUG 3 — inject status changes propagate live in Control Room
          loadInjects();
          loadTimeline();
        }
        if (data.type === 'response_submitted') {
          loadResponses();
          loadTimeline();
        }
        if (data.type === 'timeline_note') {
          loadTimeline();
        }
        if (data.type === 'exercise_control' || data.type === 'exercise_status_change' || data.type === 'exercise_updated') {
          loadExercise();
        }
      }
    };
    
    window.addEventListener('ws-message', handleWsMessage);
    
    return () => {
      leaveExercise(exerciseId);
      window.removeEventListener('ws-message', handleWsMessage);
    };
    // eslint-disable-next-line
  }, [exerciseId]);

  useEffect(() => {
    timelineEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [timeline]);

  const loadData = async () => {
    try {
      await Promise.all([
        loadExercise(),
        loadInjects(),
        loadTimeline(),
        loadResponses()
      ]);
    } catch (error) {
      console.error('Control Room loadData error:', error);
      if (error.message === 'Not authenticated') {
        toast.error('Session expired. Please log in again.');
        navigate('/login');
        return;
      }
      toast.error(error.message || 'Failed to load control room data');
    } finally {
      setLoading(false);
    }
  };

  const loadExercise = async () => {
    const data = await exercisesApi.getOne(exerciseId);
    setExercise(data);
  };

  const loadInjects = async () => {
    const data = await injectsApi.getAll(exerciseId);
    setInjects(data);
  };

  const loadTimeline = async () => {
    const data = await timelineApi.get(exerciseId);
    setTimeline(data);
  };

  const loadResponses = async () => {
    const data = await responsesApi.getAll({ exerciseId });
    setResponses(data);
  };

  const handleControlAction = async (action) => {
    try {
      await exercisesApi.control(exerciseId, action);
      toast.success(`Exercise ${action}ed`);
      loadExercise();
      loadTimeline();
    } catch (error) {
      toast.error(error.message || `Failed to ${action} exercise`);
    }
  };

  const handleTriggerInject = async (injectId) => {
    try {
      await injectsApi.trigger(injectId);
      toast.success('Inject played');
      loadInjects();
      loadTimeline();
    } catch (error) {
      toast.error(error.message || 'Failed to play inject');
    }
  };

  const handleCompleteInject = async (injectId) => {
    try {
      await injectsApi.complete(injectId);
      toast.success('Inject completed');
      loadInjects();
      loadTimeline();
    } catch (error) {
      toast.error(error.message || 'Failed to complete inject');
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteForm.message.trim() || !noteInjectId) return;

    try {
      let attachmentIds = [];
      if (noteForm.attachment) {
        const upload = await uploadsApi.upload(noteForm.attachment);
        if (upload?.file_id) {
          attachmentIds = [upload.file_id];
        }
      }
      await responsesApi.create({
        inject_id: noteInjectId,
        response_text: noteForm.message,
        note_type: noteForm.type,
        objective_met: noteForm.objectiveMet,
        participant_id: null,
        objective_id: null,
        attachments: attachmentIds,
      });
      setNoteForm({ type: 'General', message: '', objectiveMet: 'Yes', attachment: null });
      await loadResponses();
      await loadTimeline();
      toast.success('Note posted');
    } catch (error) {
      toast.error(error.message || 'Failed to post note');
    }
  };

  const handleQuickInject = async (e) => {
    e.preventDefault();
    try {
      if (editingCRInjectId) {
        // Edit mode — update existing inject
        await injectsApi.update(editingCRInjectId, {
          title: quickInjectForm.title,
          content: quickInjectForm.content,
          priority: quickInjectForm.priority,
          metadata: buildInjectMetadata(quickInjectForm),
        });
        toast.success('Inject updated');
        setEditingCRInjectId(null);
      } else if (showLibrary && selectedLibraryTemplateId) {
        const created = await injectLibraryApi.addToExercise(exerciseId, selectedLibraryTemplateId);
        const injectId = created.inject_id;
        await injectsApi.update(injectId, {
          title: quickInjectForm.title,
          content: quickInjectForm.content,
          priority: quickInjectForm.priority,
          category: quickInjectForm.category,
        });
        await injectsApi.trigger(injectId);
        toast.success('Inject created from library and played');
      } else {
        const inject = await injectsApi.create({
          title: quickInjectForm.title,
          content: quickInjectForm.content,
          priority: quickInjectForm.priority,
          exercise_id: exerciseId,
          metadata: buildInjectMetadata(quickInjectForm),
        });
        await injectsApi.trigger(inject.inject_id);
        toast.success('Inject created and played');
      }
      setQuickInjectOpen(false);
      setQuickInjectForm({
        title: '',
        content: '',
        inject_time: '',
        method: '',
        dependancy: '',
        recipient: '',
        specific_function: '',
        objective: '',
        reference: '',
        injected_by: '',
        supplemental_information: '',
        key_outcomes: '',
        priority: 'medium',
        category: 'Operations',
      });
      loadInjects();
      loadTimeline();
    } catch (error) {
      toast.error(error.message || 'Failed to create inject');
    }
  };

  // Filter and sort injects
  const openCREditInject = (inject) => {
    const meta = inject.metadata || {};
    setEditingCRInjectId(inject.inject_id);
    setQuickInjectForm({
      title: inject.title || '',
      content: inject.content || '',
      inject_time: meta['Time of Inject (UTC)'] || meta['Inject Time'] || '',
      method: meta['Method'] || '',
      dependancy: meta['Dependancy'] || '',
      recipient: meta['Recipient'] || '',
      specific_function: meta['Specific Function'] || '',
      objective: meta['Objective'] || '',
      reference: meta['Objective ID'] || '',
      injected_by: meta['Injected By'] || '',
      supplemental_information: meta['Supplemental Information'] || '',
      key_outcomes: meta['Key Outcomes'] || '',
      priority: inject.priority || 'medium',
      category: inject.category || 'Operations',
    });
    setQuickInjectOpen(true);
  };

  const filteredInjects = injects
    .filter(inject => {
      // Search filter
      if (searchFilter && !inject.title?.toLowerCase().includes(searchFilter.toLowerCase()) &&
          !inject.content?.toLowerCase().includes(searchFilter.toLowerCase())) {
        return false;
      }
      // Recipient filter
      const recipientValue = getRecipientLabel(inject);
      if (recipientFilter !== 'All' && recipientValue !== recipientFilter) {
        return false;
      }
      // Status filter
      if (statusFilter !== 'all' && inject.status !== statusFilter) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortOrder === 'newest') {
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      } else if (sortOrder === 'oldest') {
        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      } else if (sortOrder === 'priority') {
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        return (priorityOrder[a.priority] || 1) - (priorityOrder[b.priority] || 1);
      } else if (sortOrder === 'inject_time') {
        const aVal = getScheduledSortValue(a);
        const bVal = getScheduledSortValue(b);
        if (aVal === null && bVal === null) return 0;
        if (aVal === null) return 1;
        if (bVal === null) return -1;
        if (typeof aVal === 'string' || typeof bVal === 'string') {
          return String(aVal).localeCompare(String(bVal));
        }
        return aVal - bVal;
      }
      return 0;
    });

  const sentInjects = injects.filter(i => i.status !== 'draft');

  if (loading) {
    return (
      <DashboardLayout title="Control Room">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!exercise) {
    return (
      <DashboardLayout title="Control Room">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-destructive mb-4" />
            <p>Exercise not found</p>
            <Button onClick={() => navigate('/exercises')} className="mt-4">
              Back to Exercises
            </Button>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Control Room">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/exercises/${exerciseId}`)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-secondary font-bold text-xl">{exercise.name}</h1>
                <Badge className={`status-${exercise.status} border capitalize`}>
                  {exercise.status}
                </Badge>
                <TimezoneBadge timezone={exercise.timezone || 'UTC'} />
                {connected ? (
                  <Badge variant="outline" className="text-green-500 border-green-500/30">
                    <Wifi className="w-3 h-3 mr-1" /> Live
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-red-500 border-red-500/30">
                    <WifiOff className="w-3 h-3 mr-1" /> Offline
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">Mission Control Interface</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <QuickMessageButton exerciseId={exerciseId} exerciseName={exercise.name} />
            <Button onClick={() => setQuickInjectOpen(true)} data-testid="quick-inject-btn">
              <Plus className="w-4 h-4 mr-2" />
              Quick Inject
            </Button>
            {exercise.status === 'active' && (
              <>
                <Button variant="outline" onClick={() => handleControlAction('pause')}>
                  <PauseCircle className="w-4 h-4 mr-2" />
                  Pause
                </Button>
                <Button variant="destructive" onClick={() => setEndConfirmOpen(true)}>
                  <StopCircle className="w-4 h-4 mr-2" />
                  End Exercise
                </Button>
              </>
            )}
            {exercise.status === 'planned' && (
              <Button onClick={() => handleControlAction('resume')}>
                <PlayCircle className="w-4 h-4 mr-2" />
                Resume
              </Button>
            )}
            {exercise.status === 'completed' && (
              <Button variant="outline" onClick={() => setStatusChangeOpen(true)}>
                <PlayCircle className="w-4 h-4 mr-2" />
                Reactivate
              </Button>
            )}
          </div>
        </div>

        {/* Main Grid - Two columns: All Injects (wider) + Recent Activity */}
        <div className="grid grid-cols-1 2xl:grid-cols-4 gap-4">
          {/* All Injects - Takes 2 columns */}
          <Card className="2xl:col-span-3 flex flex-col h-[calc(100vh-220px)]">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-secondary text-lg flex items-center gap-2">
                  <Activity className="w-5 h-5 text-primary" />
                  All Injects ({filteredInjects.length})
                </CardTitle>
              </div>
              
              {/* Filters Row */}
              <div className="flex flex-wrap gap-2 mt-3">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px] w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search injects..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    className="pl-9 h-9"
                  />
                </div>
                
                {/* Recipient Filter */}
                <Select value={recipientFilter} onValueChange={setRecipientFilter}>
                  <SelectTrigger className="w-full sm:w-[150px] h-9">
                    <Filter className="w-3 h-3 mr-2" />
                    <SelectValue placeholder="Recipient" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Recipients</SelectItem>
                    {Array.from(new Set(injects.map(getRecipientLabel)))
                      .filter((recipient) => recipient && recipient !== '-')
                      .map(recipient => (
                      <SelectItem key={recipient} value={recipient}>{recipient}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Status Filter */}
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-[120px] h-9">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="ongoing">Ongoing</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>

                {/* Sort Order */}
                <Select value={sortOrder} onValueChange={setSortOrder}>
                  <SelectTrigger className="w-full sm:w-[130px] h-9">
                    <ArrowUpDown className="w-3 h-3 mr-2" />
                    <SelectValue placeholder="Sort" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                    <SelectItem value="priority">By Priority</SelectItem>
                    <SelectItem value="inject_time">By Inject Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            
            <CardContent className="flex-1 overflow-hidden">
              <ScrollArea className="h-full">
                {filteredInjects.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground">
                    <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No injects found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="space-y-2 pr-4 min-w-[1000px]">
                    {/* Table Header */}
                    <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground border-b pb-2 sticky top-0 bg-background">
                      <div className="col-span-1">ID</div>
                      <div className="col-span-1">Status</div>
                      <div className="col-span-1">Priority</div>
                      <div className="col-span-2">Recipient</div>
                      <div className="col-span-3">Title</div>
                      <div className="col-span-2">Time</div>
                      <div className="col-span-2">Actions</div>
                    </div>
                    
                    {/* Inject Rows */}
                    {filteredInjects.map((inject) => (
                      <div 
                        key={inject.inject_id}
                        className={`grid grid-cols-12 gap-2 items-center p-2 rounded-lg border hover:border-primary/30 transition-colors ${
                          inject.status === 'ongoing' ? 'bg-yellow-500/5 border-yellow-500/30' :
                          inject.status === 'completed' ? 'bg-green-500/5 border-green-500/30' :
                          'border-border'
                        }`}
                      >
                        {/* ID */}
                        <div className="col-span-1">
                          <span className="text-xs text-muted-foreground">
                            {getInjectIdLabel(inject)}
                          </span>
                        </div>

                        {/* Status */}
                        <div className="col-span-1">
                          <Badge 
                            variant="outline" 
                            className={`text-[10px] capitalize ${
                              inject.status === 'ongoing' ? 'border-yellow-500 text-yellow-600' :
                              inject.status === 'completed' ? 'border-green-500 text-green-600' :
                              'border-gray-400 text-muted-foreground'
                            }`}
                          >
                            {inject.status}
                          </Badge>
                        </div>
                        
                        {/* Priority */}
                        <div className="col-span-1">
                          <Badge 
                            variant="outline" 
                            className={`text-[10px] capitalize priority-${inject.priority}`}
                          >
                            {inject.priority}
                          </Badge>
                        </div>
                        
                        {/* Recipient */}
                        <div className="col-span-2">
                          <span className="text-xs text-muted-foreground truncate block">
                            {getRecipientLabel(inject)}
                          </span>
                        </div>
                        
                        {/* Title */}
                        <div className="col-span-3">
                          <p className="text-sm font-medium truncate">{inject.title}</p>
                        </div>
                        
                        {/* Time */}
                        <div className="col-span-2">
                          <p className="text-xs text-muted-foreground">
                            {getDisplayTime(inject)}
                          </p>
                        </div>
                        
                        {/* Actions */}
                        <div className="col-span-2 flex flex-wrap gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-7 px-2 text-xs"
                            onClick={() => {
                              setViewInject(inject);
                              setViewOpen(true);
                            }}
                          >
                            View
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-7 px-2 text-xs"
                            onClick={() => {
                              setNoteInjectId(inject.inject_id);
                              setNoteDialogOpen(true);
                            }}
                          >
                            Notes
                          </Button>
                          {(inject.status === 'draft' || inject.status === 'pending') && (
                            <Button 
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => handleTriggerInject(inject.inject_id)}
                            >
                              <Send className="w-3 h-3 mr-1" />
                              Play
                            </Button>
                          )}
                          {inject.status === 'ongoing' && (
                            <Button 
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs border-green-500 text-green-600 hover:bg-green-500/10"
                              onClick={() => handleCompleteInject(inject.inject_id)}
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Complete
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    </div>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Recent Activity - Right Column */}
          <Card className="flex flex-col h-[calc(100vh-220px)]">
            <CardHeader className="pb-2">
              <CardTitle className="font-secondary text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-green-500" />
                Activity & Logs
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden flex flex-col">
              <Tabs defaultValue="stream" className="flex flex-col h-full">
                <TabsList className="grid w-full grid-cols-4 mb-2">
                  <TabsTrigger value="stream" className="text-xs">
                    <Activity className="w-3 h-3 mr-1" />
                    Stream
                  </TabsTrigger>
                  <TabsTrigger value="log" className="text-xs">
                    <FileText className="w-3 h-3 mr-1" />
                    Log Entry
                  </TabsTrigger>
                  <TabsTrigger value="notes" className="text-xs">
                    <MessageSquare className="w-3 h-3 mr-1" />
                    Notes
                  </TabsTrigger>
                  <TabsTrigger value="phone" className="text-xs" data-testid="control-room-phone-tab">
                    <Phone className="w-3 h-3 mr-1" />
                    Phone
                  </TabsTrigger>
                </TabsList>

                {/* Unified Activity Stream Tab */}
                <TabsContent value="stream" className="mt-0">
                  <div style={{ height: 'calc(100vh - 341px)', overflow: 'hidden' }}>
                    <UnifiedActivityStream 
                      exerciseId={exerciseId}
                      timezone={exercise?.timezone || 'UTC'}
                      refreshInterval={15000}
                    />
                  </div>
                </TabsContent>

                {/* Activity Log Entry Tab */}
                <TabsContent value="log" className="mt-0">
                  <div style={{ height: 'calc(100vh - 341px)', overflowY: 'auto' }}>
                    <ActivityLogForm 
                      exerciseId={exerciseId}
                      onLogCreated={() => {}}
                    />
                  </div>
                </TabsContent>

                {/* Notes Tab — inject notes feed + create form */}
                <TabsContent value="notes" className="mt-0">
                  <div style={{ height: 'calc(100vh - 341px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  {/* Notes feed */}
                  <div className="flex-1 min-h-0 overflow-y-auto">
                    <div className="space-y-2 pr-1">
                      {responses.length === 0 ? (
                        <div className="py-6 text-center text-muted-foreground">
                          <MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-50" />
                          <p className="text-xs">No inject notes yet</p>
                        </div>
                      ) : (
                        responses.slice().reverse().map((r) => {
                          const inject = injects.find(i => i.inject_id === r.inject_id);
                          return (
                            <div key={r.response_id} className="p-2 rounded-lg border bg-card">
                              <div className="flex items-center justify-between gap-2 mb-0.5">
                                <span className="text-[11px] font-medium truncate">
                                  {r.sender_role === 'participant' ? `Participant: ${r.user_name}` : `${r.sender_role || 'Controller'}: ${r.user_name || ''}`}
                                </span>
                                <span className="text-[10px] text-muted-foreground shrink-0">
                                  {new Date(r.submitted_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                              {inject && (
                                <p className="text-[10px] text-muted-foreground mb-0.5 truncate">
                                  Re: {inject.title?.split('\n')[0]}
                                </p>
                              )}
                              <p className="text-xs whitespace-pre-wrap">{r.response_text}</p>
                              {r.note_type && <Badge variant="outline" className="text-[9px] mt-1">{r.note_type}</Badge>}
                              {!!(r.attachments || []).length && (
                                <div className="mt-1 flex gap-1 flex-wrap">
                                  {(r.attachments_meta?.length ? r.attachments_meta : (r.attachments || []).map((fid) => ({ file_id: fid }))).map((att) => {
                                    const token = localStorage.getItem('ems_token');
                                    const fileId = att.file_id || att;
                                    const filename = att.filename || fileId;
                                    const fileUrl = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${fileId}?token=${encodeURIComponent(token || '')}`;
                                    return (
                                      <a key={fileId} className="text-[10px] underline text-primary" href={fileUrl} target="_blank" rel="noreferrer">{filename}</a>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                  
                  {/* Note creation form - pinned at bottom */}
                  <div className="shrink-0 pt-2 mt-2 border-t space-y-2">
                    <Select value={noteInjectId} onValueChange={setNoteInjectId}>
                      <SelectTrigger className="w-full h-7 text-xs">
                        <SelectValue placeholder="Select inject for note" />
                      </SelectTrigger>
                      <SelectContent>
                        {sentInjects.map((inj) => (
                          <SelectItem key={inj.inject_id} value={inj.inject_id}>
                            {inj.title?.split('\n')[0]?.substring(0, 50)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="grid grid-cols-2 gap-1.5">
                      <Select value={noteForm.type} onValueChange={(value) => setNoteForm({ ...noteForm, type: value })}>
                        <SelectTrigger className="h-7 text-xs" data-testid="note-type-select">
                          <SelectValue placeholder="Note type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Key Activities">Key Activities</SelectItem>
                          <SelectItem value="General">General</SelectItem>
                          <SelectItem value="Outcomes">Outcomes</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={noteForm.objectiveMet} onValueChange={(value) => setNoteForm({ ...noteForm, objectiveMet: value })}>
                        <SelectTrigger className="h-7 text-xs" data-testid="note-objective-select">
                          <SelectValue placeholder="Objective?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Textarea
                      placeholder="Type a note..."
                      value={noteForm.message}
                      onChange={(e) => setNoteForm({ ...noteForm, message: e.target.value })}
                      rows={2}
                      className="text-xs min-h-[50px]"
                      data-testid="note-message-input"
                    />
                    <Input
                      type="file"
                      className="h-7 text-xs"
                      onChange={(e) => setNoteForm({ ...noteForm, attachment: e.target.files?.[0] || null })}
                      data-testid="note-attachment-input"
                    />
                    <Button
                      type="button"
                      size="sm"
                      className="w-full h-7 text-xs"
                      disabled={!noteForm.message.trim() || !noteInjectId}
                      onClick={handleAddNote}
                      data-testid="add-note-submit"
                    >
                      Save Note
                    </Button>
                  </div>
                  </div>
                </TabsContent>

                {/* Phone / Softphone Tab */}
                <TabsContent value="phone" className="mt-0">
                  <div style={{ height: 'calc(100vh - 341px)', overflowY: 'auto' }}>
                    <Softphone embedded />
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Notes Dialog */}
        <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
          <DialogContent className="max-w-2xl" onInteractOutside={(e) => e.preventDefault()} data-testid="control-room-notes-dialog">
            <DialogHeader>
              <DialogTitle className="font-secondary">Notes</DialogTitle>
            </DialogHeader>

            <div className="space-y-3">
              <div className="border rounded-lg p-3 max-h-[320px] overflow-auto space-y-3">
                {responses
                  .filter((r) => r.inject_id === noteInjectId)
                  .map((r) => (
                    <div key={r.response_id} className="p-3 rounded-lg border">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium">
                          {r.sender_role === 'participant' ? `Participant: ${r.user_name}` : `${r.sender_role}: ${r.user_name}`}
                        </div>
                        <div className="text-xs text-muted-foreground">{new Date(r.submitted_at).toLocaleString()}</div>
                      </div>
                      <p className="text-sm mt-2 whitespace-pre-wrap">{r.response_text}</p>
                      {(r.note_type || r.objective_met) && (
                        <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-muted-foreground">
                          {r.note_type && (
                            <Badge variant="outline" className="text-xs">{r.note_type}</Badge>
                          )}
                          {r.objective_met && <span>Objective met: {r.objective_met}</span>}
                        </div>
                      )}

                      {!!(r.attachments || []).length && (
                        <div className="mt-2 space-y-1">
                          {(r.attachments_meta?.length ? r.attachments_meta : (r.attachments || []).map((fid) => ({ file_id: fid }))).map((att) => {
                            const token = localStorage.getItem('ems_token');
                            const fileId = att.file_id || att;
                            const filename = att.filename || fileId;
                            const contentType = att.content_type || '';
                            const isImage = contentType.startsWith('image/');
                            const fileUrl = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${fileId}?token=${encodeURIComponent(token || '')}`;
                            return (
                              <div key={fileId} className="flex items-center gap-2">
                                <a
                                  className="text-xs underline text-primary"
                                  href={fileUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  data-testid="control-room-attachment-download-link"
                                >
                                  {filename}
                                </a>
                                {isImage && (
                                  <img
                                    src={fileUrl}
                                    alt={filename}
                                    className="h-12 w-12 rounded-md object-cover border"
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}

                {responses.filter((r) => r.inject_id === noteInjectId).length === 0 && (
                  <div className="text-sm text-muted-foreground">No notes yet</div>
                )}
              </div>

              <form onSubmit={handleAddNote} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={noteForm.type} onValueChange={(value) => setNoteForm({ ...noteForm, type: value })}>
                      <SelectTrigger data-testid="note-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Key Activities">Key Activities</SelectItem>
                        <SelectItem value="General">General</SelectItem>
                        <SelectItem value="Outcomes">Outcomes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Did you meet the objective?</Label>
                    <Select value={noteForm.objectiveMet} onValueChange={(value) => setNoteForm({ ...noteForm, objectiveMet: value })}>
                      <SelectTrigger data-testid="note-objective-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Message</Label>
                  <Textarea
                    placeholder="Type a note..."
                    value={noteForm.message}
                    onChange={(e) => setNoteForm({ ...noteForm, message: e.target.value })}
                    rows={4}
                    data-testid="note-textarea"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Attachment (optional)</Label>
                  <Input
                    type="file"
                    onChange={(e) => setNoteForm({ ...noteForm, attachment: e.target.files?.[0] || null })}
                    data-testid="note-attachment-input"
                  />
                </div>
                <Button type="submit" disabled={!noteForm.message.trim() || !noteInjectId} className="w-full" data-testid="add-note-submit">
                  Save Note
                </Button>
              </form>
            </div>
          </DialogContent>
        </Dialog>

        {/* Quick Inject Dialog */}
        <Dialog open={quickInjectOpen} onOpenChange={(open) => {
          setQuickInjectOpen(open);
          if (!open) {
            setShowLibrary(false);
            setLibrarySearch('');
            setLibraryCategory('all');
            setLibraryCompany('all');
            setSelectedLibraryTemplateId(null);
            setEditingCRInjectId(null);
          }
        }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-secondary">{editingCRInjectId ? 'Edit Inject' : 'Quick Inject'}</DialogTitle>
            </DialogHeader>
            
            <div className="flex gap-2 mb-2">
              <Button
                type="button"
                size="sm"
                variant={!showLibrary ? 'default' : 'outline'}
                onClick={() => setShowLibrary(false)}
              >
                Manual Entry
              </Button>
              <Button
                type="button"
                size="sm"
                variant={showLibrary ? 'default' : 'outline'}
                onClick={() => setShowLibrary(true)}
              >
                From Library
              </Button>
            </div>

            {showLibrary ? (
              <div className="space-y-3">
                <Input
                  placeholder="Search inject library..."
                  value={librarySearch}
                  onChange={(e) => setLibrarySearch(e.target.value)}
                />

                <div className="grid grid-cols-2 gap-2">
                  <Select value={libraryCategory} onValueChange={setLibraryCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {libraryCategories.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={libraryCompany} onValueChange={setLibraryCompany}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Companies" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Companies</SelectItem>
                      {libraryCompanies.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <ScrollArea className="h-[250px] border rounded-lg" style={{ overflow: 'auto' }}>
                  {libraryTemplates.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-4 text-center">
                      {librarySearch || libraryCategory !== 'all' || libraryCompany !== 'all' 
                        ? 'No templates found' 
                        : 'Select a filter or search to load templates.'}
                    </p>
                  ) : (
                    <div className="divide-y">
                      {libraryTemplates.slice(0, 50).map((t) => {
                        const isSelected = selectedLibraryTemplateId === t.template_id;
                        return (
                          <label
                            key={t.template_id}
                            className={`p-3 flex items-start gap-3 cursor-pointer transition-colors ${
                              isSelected ? 'bg-primary/10' : 'hover:bg-muted/50'
                            }`}
                            data-testid={`library-template-${t.template_id}`}
                          >
                            <input
                              type="radio"
                              name="library-template"
                              checked={isSelected}
                              onChange={() => handleSelectTemplate(t)}
                              className="mt-1 accent-primary cursor-pointer"
                              data-testid={`library-checkbox-${t.template_id}`}
                            />
                            <div className="flex-1 min-w-0" onClick={() => handleSelectTemplate(t)}>
                              <p className="text-sm font-medium line-clamp-1">
                                {t.inject_information || t.objective || t.title || 'Untitled'}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {t.category} {'\u2022'} {t.company || '-'}
                              </p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
              </div>
            ) : (
              <form onSubmit={handleQuickInject} className="space-y-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={quickInjectForm.title}
                    onChange={(e) => setQuickInjectForm({...quickInjectForm, title: e.target.value})}
                    placeholder="Emergency Update"
                    required
                    data-testid="quick-inject-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Inject Information *</Label>
                  <Textarea
                    value={quickInjectForm.content}
                    onChange={(e) => setQuickInjectForm({...quickInjectForm, content: e.target.value})}
                    placeholder="Describe the scenario..."
                    rows={3}
                    required
                    data-testid="quick-inject-information-input"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Inject Time (UTC)</Label>
                    <Input
                      value={quickInjectForm.inject_time}
                      onChange={(e) => setQuickInjectForm({ ...quickInjectForm, inject_time: e.target.value })}
                      placeholder="Oct 17th @ 10:00"
                      data-testid="quick-inject-time-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Method</Label>
                    <Input
                      value={quickInjectForm.method}
                      onChange={(e) => setQuickInjectForm({ ...quickInjectForm, method: e.target.value })}
                      data-testid="quick-inject-method-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Dependancy</Label>
                    <Input
                      value={quickInjectForm.dependancy}
                      onChange={(e) => setQuickInjectForm({ ...quickInjectForm, dependancy: e.target.value })}
                      data-testid="quick-inject-dependancy-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Recipient</Label>
                    <Input
                      value={quickInjectForm.recipient}
                      onChange={(e) => setQuickInjectForm({ ...quickInjectForm, recipient: e.target.value })}
                      data-testid="quick-inject-recipient-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Specific Function</Label>
                    <Input
                      value={quickInjectForm.specific_function}
                      onChange={(e) => setQuickInjectForm({ ...quickInjectForm, specific_function: e.target.value })}
                      data-testid="quick-inject-specific-function-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Reference (Objective ID)</Label>
                    <Input
                      value={quickInjectForm.reference}
                      onChange={(e) => setQuickInjectForm({ ...quickInjectForm, reference: e.target.value })}
                      data-testid="quick-inject-reference-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Injected By</Label>
                    <Input
                      value={quickInjectForm.injected_by}
                      onChange={(e) => setQuickInjectForm({ ...quickInjectForm, injected_by: e.target.value })}
                      data-testid="quick-inject-injected-by-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Objective</Label>
                  <Textarea
                    value={quickInjectForm.objective}
                    onChange={(e) => setQuickInjectForm({ ...quickInjectForm, objective: e.target.value })}
                    rows={3}
                    data-testid="quick-inject-objective-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Supplemental Information</Label>
                  <Textarea
                    value={quickInjectForm.supplemental_information}
                    onChange={(e) => setQuickInjectForm({ ...quickInjectForm, supplemental_information: e.target.value })}
                    rows={3}
                    data-testid="quick-inject-supplemental-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Key Outcomes</Label>
                  <Textarea
                    value={quickInjectForm.key_outcomes}
                    onChange={(e) => setQuickInjectForm({ ...quickInjectForm, key_outcomes: e.target.value })}
                    rows={3}
                    data-testid="quick-inject-key-outcomes-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select 
                      value={quickInjectForm.priority}
                      onValueChange={(v) => setQuickInjectForm({...quickInjectForm, priority: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select 
                      value={quickInjectForm.category}
                      onValueChange={(v) => setQuickInjectForm({...quickInjectForm, category: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {INJECT_CATEGORIES.filter(c => c !== 'All').map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setQuickInjectOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    <Send className="w-4 h-4 mr-2" />
                    {editingCRInjectId ? 'Save Changes' : 'Send Immediately'}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        <InjectQuickViewModal
          open={viewOpen}
          onOpenChange={setViewOpen}
          inject={viewInject}
          onOpenNotes={() => {
            if (viewInject?.inject_id) {
              setNoteInjectId(viewInject.inject_id);
              setNoteDialogOpen(true);
            }
          }}
          onEdit={openCREditInject}
        />

        {/* End Exercise Confirmation */}
        <Dialog open={endConfirmOpen} onOpenChange={setEndConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-secondary text-destructive">End Exercise?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to end this exercise? This will mark it as completed.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEndConfirmOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => {
                  handleControlAction('end');
                  setEndConfirmOpen(false);
                }}
              >
                Yes, End Exercise
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Status Change Dialog */}
        <Dialog open={statusChangeOpen} onOpenChange={setStatusChangeOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-secondary">Change Exercise Status</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                Current: <Badge className={`status-${exercise?.status} border capitalize ml-2`}>{exercise?.status}</Badge>
              </p>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant="outline"
                  onClick={async () => {
                    await exercisesApi.control(exerciseId, 'draft');
                    toast.success('Set to draft');
                    loadExercise();
                    setStatusChangeOpen(false);
                  }}
                  disabled={exercise?.status === 'draft'}
                >
                  Draft
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    await exercisesApi.control(exerciseId, 'start');
                    toast.success('Activated');
                    loadExercise();
                    setStatusChangeOpen(false);
                  }}
                  disabled={exercise?.status === 'active'}
                >
                  Active
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    await exercisesApi.control(exerciseId, 'end');
                    toast.success('Completed');
                    loadExercise();
                    setStatusChangeOpen(false);
                  }}
                  disabled={exercise?.status === 'completed'}
                >
                  Completed
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
